/**
 * Editor state and reducer. Pure: the UI dispatches actions, tests drive it directly.
 *
 * The layout may be temporarily invalid while editing (e.g. a half-typed slot ID); the
 * shared schema reports issues and export is blocked until they are fixed.
 */
import { boxToPosition, slotGeometry, type LayoutFile, type LayoutSlot, type Rect } from '@ioda/shared';
import { cameraSlot } from './templates.ts';

/** Undo depth. Layouts are tiny; this is about bounding memory, not performance. */
export const HISTORY_LIMIT = 200;

export interface EditorState {
  layout: LayoutFile;
  selectedSlotId: string | null;
  past: LayoutFile[];
  future: LayoutFile[];
  /** Changed since the last load or export. */
  dirty: boolean;
}

export type SlotPatch = Partial<LayoutSlot>;
export type LayoutPatch = Partial<Pick<LayoutFile, 'layoutId' | 'name' | 'slotCount'>>;

export type EditorAction =
  | { type: 'load'; layout: LayoutFile }
  | { type: 'markSaved' }
  | { type: 'select'; slotId: string | null }
  /** Snapshot the layout once at the start of a drag; the drag's updates use `record: false`. */
  | { type: 'beginGesture' }
  | { type: 'updateSlot'; slotId: string; patch: SlotPatch; record?: boolean }
  | { type: 'setSlotBox'; slotId: string; rect: Rect; record?: boolean }
  | { type: 'updateLayout'; patch: LayoutPatch }
  | { type: 'addSlot' }
  | { type: 'duplicateSlot'; slotId: string }
  | { type: 'removeSlot'; slotId: string }
  | { type: 'undo' }
  | { type: 'redo' };

export function initialState(layout: LayoutFile): EditorState {
  return { layout, selectedSlotId: layout.slots[0]?.slotId ?? null, past: [], future: [], dirty: false };
}

/** The slot's item box on the canvas (what the editor drags and resizes). */
export function slotBox(slot: LayoutSlot): Rect {
  return slotGeometry(slot).boxBounds;
}

/**
 * The fields that place an unrotated slot's box at `rect`. With bounds, the box is the
 * bounds; without bounds the size is the source size and only the position changes.
 */
export function boxPatch(slot: LayoutSlot, rect: Rect): SlotPatch {
  const hasBounds = slot.boundsType !== 'OBS_BOUNDS_NONE';
  const width = hasBounds ? Math.max(1, rect.width) : slotBox(slot).width;
  const height = hasBounds ? Math.max(1, rect.height) : slotBox(slot).height;
  const p = boxToPosition({ x: rect.x, y: rect.y, width, height }, slot.alignment);
  return hasBounds
    ? { positionX: p.x, positionY: p.y, boundsWidth: width, boundsHeight: height }
    : { positionX: p.x, positionY: p.y };
}

/** A slot ID not used yet, from the semantic names first, then `slot-n`. */
export function nextSlotId(layout: LayoutFile, preferred: readonly string[] = ['host', 'guest-1', 'guest-2', 'guest-3', 'guest-4', 'guest-5', 'main', 'speaker', 'share']): string {
  const used = new Set(layout.slots.map((s) => s.slotId));
  const free = preferred.find((id) => !used.has(id));
  if (free) return free;
  for (let n = 1; ; n++) if (!used.has(`slot-${n}`)) return `slot-${n}`;
}

function visibleCount(layout: LayoutFile): number {
  return layout.slots.filter((s) => s.hidden !== true).length;
}

function commit(state: EditorState, layout: LayoutFile, record = true, selectedSlotId = state.selectedSlotId): EditorState {
  return {
    layout,
    selectedSlotId,
    past: record ? [...state.past, state.layout].slice(-HISTORY_LIMIT) : state.past,
    future: record ? [] : state.future,
    dirty: true,
  };
}

function withSlots(layout: LayoutFile, slots: LayoutSlot[], syncCount: boolean): LayoutFile {
  const next = { ...layout, slots };
  // slotCount follows the visible slots unless the user set it to something else.
  return syncCount ? { ...next, slotCount: Math.max(1, visibleCount(next)) } : next;
}

function countIsSynced(layout: LayoutFile): boolean {
  return layout.slotCount === Math.max(1, visibleCount(layout));
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  const { layout } = state;
  switch (action.type) {
    case 'load':
      return initialState(action.layout);

    case 'markSaved':
      return { ...state, dirty: false };

    case 'select':
      return { ...state, selectedSlotId: action.slotId };

    case 'beginGesture':
      return { ...state, past: [...state.past, layout].slice(-HISTORY_LIMIT), future: [] };

    case 'updateSlot': {
      const index = layout.slots.findIndex((s) => s.slotId === action.slotId);
      if (index < 0) return state;
      const synced = countIsSynced(layout);
      const slots = layout.slots.map((s, i) => (i === index ? { ...s, ...action.patch } : s));
      const renamed = action.patch.slotId !== undefined && state.selectedSlotId === action.slotId ? action.patch.slotId : state.selectedSlotId;
      return commit(state, withSlots(layout, slots, synced), action.record ?? true, renamed);
    }

    case 'setSlotBox': {
      const slot = layout.slots.find((s) => s.slotId === action.slotId);
      if (!slot || slot.rotation !== 0) return state;
      return editorReducer(state, { type: 'updateSlot', slotId: action.slotId, patch: boxPatch(slot, action.rect), record: action.record ?? true });
    }

    case 'updateLayout':
      return commit(state, { ...layout, ...action.patch });

    case 'addSlot': {
      const synced = countIsSynced(layout);
      const order = Math.max(-1, ...layout.slots.map((s) => s.order)) + 1;
      const zIndex = Math.max(0, ...layout.slots.map((s) => s.zIndex));
      const slot = { ...cameraSlot({ slotId: nextSlotId(layout), x: 660, y: 371.25, width: 600, height: 337.5 }, order), zIndex };
      return commit(state, withSlots(layout, [...layout.slots, slot], synced), true, slot.slotId);
    }

    case 'duplicateSlot': {
      const source = layout.slots.find((s) => s.slotId === action.slotId);
      if (!source) return state;
      const synced = countIsSynced(layout);
      const order = Math.max(...layout.slots.map((s) => s.order)) + 1;
      const copy: LayoutSlot = { ...source, slotId: nextSlotId(layout), order, positionX: source.positionX + 20, positionY: source.positionY + 20 };
      return commit(state, withSlots(layout, [...layout.slots, copy], synced), true, copy.slotId);
    }

    case 'removeSlot': {
      if (!layout.slots.some((s) => s.slotId === action.slotId)) return state;
      const synced = countIsSynced(layout);
      const slots = layout.slots.filter((s) => s.slotId !== action.slotId);
      const selected = state.selectedSlotId === action.slotId ? (slots[0]?.slotId ?? null) : state.selectedSlotId;
      return commit(state, withSlots(layout, slots, synced), true, selected);
    }

    case 'undo': {
      const prev = state.past.at(-1);
      if (!prev) return state;
      const selected = prev.slots.some((s) => s.slotId === state.selectedSlotId) ? state.selectedSlotId : (prev.slots[0]?.slotId ?? null);
      return { layout: prev, selectedSlotId: selected, past: state.past.slice(0, -1), future: [layout, ...state.future], dirty: true };
    }

    case 'redo': {
      const next = state.future[0];
      if (!next) return state;
      const selected = next.slots.some((s) => s.slotId === state.selectedSlotId) ? state.selectedSlotId : (next.slots[0]?.slotId ?? null);
      return { layout: next, selectedSlotId: selected, past: [...state.past, layout], future: state.future.slice(1), dirty: true };
    }
  }
}
